<template>
  <div>
    <aw-spinner v-if="isAdultContentHidden" />
    <div v-else-if="productId" :class="$style.productPage">
      <aw-breadcrumb-version2 :class="$style.productPageBreadcrumb" />
      <div :class="$style.productDetailsGridRow">
        <div :class="$style.productDetailsGridRowDetails">
          <div :class="$style.productDetailsGridDetails">
            <div id="productDetails" :class="$style.productDetails">
              <aw-product-detail
                v-if="productId"
                :product-data="productData"
                :review-link="hasReviewCms ? Navigation[ID_PRODUCT_REVIEWS] : ''"
              />
              <div v-else :class="$style.preloadDetails">
                <aw-spinner />
              </div>
              <div v-if="stockInfos.length" :class="$style.productStockCheck">
                <lv-button
                  ref="stockInfoButton"
                  :class="['hideStockInfoSidebar', $style.productStockCheckButton]"
                  shrink
                  styling="link-primary-version2"
                  :aria-expanded="isStockInfoOpened"
                  @click="setStockInfoSidebar(!isStockInfoOpened)"
                >
                  {{ $awt('aw.common.product.check_stock') }}
                </lv-button>
              </div>
              <div v-if="bundleProductList.length">
                <ul :class="['ulReset', $style.bundleWrapper]">
                  <li v-for="bundle in bundleProductList" :key="bundle.packageId">
                    <aw-product-bundle :bundle="bundle" />
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div :class="$style.productDetailsGridGallery">
            <aw-product-gallery-version2
              :id-flix-minisite="ID_FLIX_MINISITE"
              :product-data="productData"
            />
          </div>
        </div>
        <div>
          <lv-heading
            :id="componentId"
            tag="h2"
            level="2"
            version2
          >
            <span
              :class="$style.productInformationsBlockTitle"
              v-text="$awt('aw.common.product.product_informations_block.title')"
            />
          </lv-heading>
          <div v-if="showProductDescriptionAboveTabs && description" :class="$style.descriptionBlock">
            <lv-heading
              tag="h3"
              level="3"
              version2
            >
              <span
                :class="$style.descriptionBlockTitle"
                v-text="$awt('aw.common.product.description_block.title')"
              />
            </lv-heading>
            <div
              :class="$style.descriptionBlockBody"
              v-html="description"
            />
          </div>

          <aw-tab-accordion
            :aria-labelledby="componentId"
            :visible-tab-items="visibleTabItems"
            :class="$style.productInformationsBlock"
          >
            <template #tab_description>
              {{ $awt('aw.common.product.description.title') }}
            </template>
            <template #tabpanel_description>
              <div :class="$style.description">
                <div v-if="description && !showProductDescriptionAboveTabs" v-html="description" />
                <aw-product-flix-media
                  v-if="eanCode && productId"
                  :ean-code="eanCode"
                  :product-id="productId"
                  :class="{[$style.flixWrapper]: description}"
                />
              </div>
            </template>
            <template #tab_parameters>
              {{ $awt('aw.common.product.parameters.title') }}
            </template>
            <template #tabpanel_parameters>
              <aw-product-parameters :parameters="parameters" />
            </template>
            <template #tab_ingredients>
              {{ $awt('aw.common.product.informations.accordion.ingredients') }}
            </template>
            <template #tabpanel_ingredients>
              <aw-product-informations />
            </template>
            <template #tab_documents>
              {{ $awt('aw.common.product.informations.accordion.documents') }}
            </template>
            <template #tabpanel_documents>
              <aw-product-documents :documents="documents" />
            </template>
            <template #tab_cetelem>
              {{ $awt('aw.common.product.informations.accordion.cetelem') }}
            </template>
            <template #tabpanel_cetelem>
              <aw-product-cetelem :credit-types="creditTypes" :purchase-amount="priceGrossDiscountedValue" />
            </template>
          </aw-tab-accordion>

          <a v-if="blocks.showDataEraserBlock" :href="$awt('aw.common.product.data_eraser_info.url')" target="_blank">
            <img
              :class="$style.productDataEraserImage"
              :alt="$awt('aw.common.product.data_eraser_info.alt_text')"
              :src="$addWebpPostfixIfSupported({ url: dataEraserInfo, webpUrl: dataEraserInfoWebp })"
              loading="lazy"
            >
          </a>

          <aw-cms-page
            v-for="content in contentData"
            :key="content.id"
            :content-data="content"
            force-full-width
          />
        </div>
      </div>
      <aw-arukereso-tracking page="product_detail" />
    </div>
    <client-only>
      <Teleport to="#awTeleportTargetSubHeader">
        <transition v-bind="transitionX">
          <aw-product-stock-info-sidebar
            v-if="isStockInfoOpened"
            v-model:show="isStockInfoOpened"
            :stock-infos="stockInfos"
            :logistic-g-stock="0"
            @update:show="setStockInfoSidebar"
          />
        </transition>
      </Teleport>
    </client-only>
  </div>
</template>
<script setup>
  import { computed, defineAsyncComponent, onMounted, onUnmounted, ref, watch } from 'vue';
  import { useAsyncData, useId, useNuxtApp } from 'nuxt/app';
  import { useJsonld } from '#jsonld';
  import { useProductReviews } from '~~/common/composables/useProductReviews';
  import { LvButton, LvHeading } from '~~/common/components/loginet-vue-shop/index';
  import { HT_LAST_VIEWED_PRODUCTS, HT_PRODUCT_REVIEW } from '~~/common/config/ContentPageTypeConfig';
  import { ID_PRODUCT_REVIEWS, Navigation } from '~~/common/config/Navigation';
  import AwSpinner from '~~/common/components/Common/AwSpinner.vue';
  import AwProductInformations from '~~/common/components/Page/Product/AwProductInformations';
  import AwProductParameters from '~~/common/components/Page/Product/AwProductParameters';
  import AwProductDocuments from '~~/common/components/Page/Product/AwProductDocuments';
  import AwProductDetail from '~~/common/components/Page/Product/AwProductDetail';
  import AwProductGalleryVersion2 from '~~/common/components/Page/Product/AwProductGalleryVersion2';
  import AwBreadcrumbVersion2 from '~~/common/components/Common/AwBreadcrumbVersion2.vue';
  import AwProductStockInfoSidebar from '~~/common/components/Page/Product/AwProductStockInfoSidebar';
  import AwProductFlixMedia from '~~/common/components/Page/Product/AwProductFlixMedia.vue';
  import { useProductData } from '~~/common/composables/useProductData';
  import { getThumbImageByVariant, isReviewVisible } from '~~/common/utils/productData';
  import { useDeliveryStore } from '~~/shop/app/stores/delivery';
  import { useProductStore } from '~~/common/stores/product';
  import { useContentsStore } from '~~/common/stores/contents';
  import { useUserStore } from '~~/common/stores/user';
  import dataEraserInfo from '~~/common/assets/images/data_eraser_info.png';
  import dataEraserInfoWebp from '~~/common/assets/images/data_eraser_info.png.webp';
  import { deepMerge } from '~~/common/utils/deepMerge';
  import { transitionX } from '~~/common/utils/transition';
  import { tabAccordionAccordionBordered, tabAccordionTabWhite } from '~~/common/components/AwTabAccordion.vue';
  import { useProductsStore } from '~~/common/stores/products';
  import AwArukeresoTracking from '~~/shop/app/components/Common/Arukereso/AwArukeresoTracking.vue';
  import defaultImage from '~~/common/assets/images/shop-entity-default.jpg';

  const AwCmsPage = defineAsyncComponent(() => import('~~/common/components/Page/Content/AwCmsPage.vue'));
  const AwProductBundle = defineAsyncComponent(() => import('~~/common/components/AwProductBundle.vue'));
  const AwProductCetelem = defineAsyncComponent(() => import('~~/common/components/Page/Product/AwProductCetelem.vue'));
  const AwTabAccordion = defineAsyncComponent(() => import('~~/common/components/AwTabAccordion.vue'));

  defineOptions({
    name: 'ProductPage',
  });

  const props = defineProps({
    product: {
      type: Object,
      required: true,
    },
  });

  const componentId = useId();
  const {
    $date,
    $awMergedConfig,
  } = useNuxtApp();

  const productStore = useProductStore();

  const isStockInfoOpened = ref(false);
  const stockInfoButton = ref(null);
  const bundleProductList = shallowRef([]);
  const ID_FLIX_MINISITE = 'flix-minisite';

  const productData = computed(() => {
    return props.product || {};
  });

  const {
    productId,
    productName,
    variant,
    variantId,
    eanCode,
    blocks,
    productCategoryId,
    stockInfos,
    documents,
    priceGrossDiscountedValue,
    creditTypes,
  } = useProductData({ productData });

  const hasReviewCms = computed(() => {
    return contentData.value.some((cd) => {
      return cd.layoutBlocks.some((lb) => {
        return lb.components.some((cp) => {
          return cp.type === HT_PRODUCT_REVIEW;
        });
      });
    });
  });
  const contentData = computed(() => {
    const originalContents = useContentsStore().dataByResourceId || [];
    const isRatingVisible = isReviewVisible(productData.value?.reviewable);
    const { isEcomUser } = useUserStore();
    if (isRatingVisible && isEcomUser) {
      return originalContents;
    }
    const newContents = JSON.parse(JSON.stringify(originalContents));
    newContents.forEach((cd) => {
      cd.layoutBlocks.forEach((lb) => {
        lb.components = lb.components.filter((cp) => {
          switch (cp.type) {
            case HT_PRODUCT_REVIEW:
              return isRatingVisible;
            case HT_LAST_VIEWED_PRODUCTS:
              return isEcomUser;
            default:
              return true;
          }
        });
      });
      cd.layoutBlocks = cd.layoutBlocks.filter((lb) => {
        return lb.components.length;
      });
    });
    return newContents;
  });
  const isAdultContentHidden = computed(() => {
    const productAdultsOnly = productData.value?.adultsOnly;
    const ageConfirmed = useUserStore().data?.personalInfo?.ageConfirmed;
    return productAdultsOnly && !ageConfirmed;
  });
  const whiteBordered = deepMerge(
    {},
    tabAccordionTabWhite,
    tabAccordionAccordionBordered,
  );
  const visibleTabItems = computed(() => {
    const { flixmediaDistributor } = $awMergedConfig;
    const {
      showParameterListBlock,
      showDescriptionBlock,
      showIngredientsBlock,
      showIngredientsDetailedBlock,
      showAllergensBlock,
      showAllergensDetailedBlock,
      showNutritionBlock,
      showDocumentsBlock,
      showCetelemBlock,
    } = blocks.value;
    return {
      parameters: {
        isVisible: showParameterListBlock,
        ...whiteBordered,
      },
      description: {
        isVisible: showDescriptionBlock || !!flixmediaDistributor,
      },
      ingredients: {
        isVisible: showIngredientsBlock || showIngredientsDetailedBlock || showAllergensBlock || showAllergensDetailedBlock || showNutritionBlock,
        ...whiteBordered,
      },
      documents: {
        isVisible: showDocumentsBlock,
        ...whiteBordered,
      },
      cetelem: {
        isVisible: showCetelemBlock,
      },
    };
  });

  const showProductDescriptionAboveTabs = computed(() => {
    return $awMergedConfig.showProductDescriptionAboveTabs;
  });

  const descriptionWatcher = computed(() => `productdescription_${productId.value}_${variantId.value}_${blocks.value.showDescriptionBlock}`);
  const parameterWatcher = computed(() => `productparameter_${productId.value}_${variantId.value}_${blocks.value.showParameterListBlock}`);

  const [
    { data: description },
    { data: parameters },
    { data: reviews },
  ] = await Promise.all([
    useAsyncData(
      descriptionWatcher.value,
      async () => {
        if (blocks.value.showDescriptionBlock) {
          return (await productStore.fetchDescription({
            id: productId.value,
            variantId: variantId.value,
          }))?.description;
        } else {
          return '';
        }
      },
      {
        watch: [
          descriptionWatcher,
        ],
        default: () => '',
      },
    ),
    useAsyncData(
      parameterWatcher.value,
      async () => {
        if (blocks.value.showParameterListBlock) {
          return (await productStore.fetchParameterList({
            id: productId.value,
            variantId: variantId.value,
          }))?.parameters;
        } else {
          return [];
        }
      },
      {
        watch: [
          parameterWatcher,
        ],
        default: () => [],
      },
    ),
    useProductReviews(productData),
  ]);

  useJsonld(() => {
    const product = productData.value;
    const pVariant = variant.value;
    const thumbImage = getThumbImageByVariant(variant.value);
    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      brand: product.brandName,
      category: product.categories.map(category => category.name).join('/'),
      sku: pVariant.sku,
      ...(reviews.value != null && reviews.value.length > 0 && product.reviewable !== 'disabled'
        ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: product.reviewSum.average,
            ratingCount: reviews.value.length,
            bestRating: 5,
            worstRating: 1,
          },
          review: reviews.value.map(review => ({
            '@type': 'Review',
            author: review.editorName,
            datePublished: $date(review.postDate, {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            }),
            reviewBody: review.body,
            reviewRating: {
              '@type': 'Rating',
              bestRating: 5,
              ratingValue: review.rating,
              worstRating: 1,
            },
          })),
        }
        : {}
      ),
      description: description.value,
      name: productName.value,
      image: thumbImage.length > 0 ? thumbImage[0].url : defaultImage,
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        price: pVariant.price.grossDiscounted,
        priceCurrency: pVariant.price.currency,
      },
    };
  });

  onMounted(() => {
    const contentsStore = useContentsStore();

    contentsStore.fetchStaticContentByResourceId({
      type: 'product_page_content',
      resourceId: variant.value.sku,
    });
    productStore.fetchStockInfos({
      id: productId.value,
      variantId: variantId.value,
    });
    productStore.fetchProductCategory({
      categoryId: productCategoryId.value,
    });
    watch(
      () => {
        return useDeliveryStore().setup;
      },
      calculateBundles,
      {
        immediate: true,
      },
    );

    window.addEventListener('popstate', onPopState);
  });

  onUnmounted(() => {
    window.removeEventListener('popstate', onPopState);
  });

  async function calculateBundles () {
    const bundles = await productStore.fetchBundles({
      id: productId.value,
      variantId: variantId.value,
    });
    bundleProductList.value = bundles.results;
  }

  function setStockInfoSidebar (value) {
    isStockInfoOpened.value = value;
    if (!value) {
      stockInfoButton.value?.$el?.focus();
    }
  }

  function onPopState () {
    const productsStore = useProductsStore();
    productsStore.enableProductListHistoryInformation();
  }


</script>

<style module lang="scss" rel="stylesheet/scss">
.productPage {
  margin-bottom: 20px;
  @include desktop-small(min) {
    margin-bottom: 60px;
  }

  .productDataEraserImage {
    margin: 12px 0;
  }

  &Breadcrumb {
    margin: 0 0 40px;
  }
}

.productStockCheck {
  font-family: $secondary-font;
  margin-top: 20px;
  padding-bottom: 20px;
  border-bottom: 1px dashed $color-dash-separator;

  &Button {
    font-size: 12px;
    line-height: 16px;
    text-decoration: underline;

    @include tablet(min) {
      font-size: 14px;
      line-height: 20px;
    }
  }
}

.productDetailsGridRow {
  position: relative;

  @include tablet(min) {
    display: flex;
    flex-direction: column;
    gap: 0 18px;
  }

  &Details {
    display: grid;
    grid-template-rows: auto auto;
    grid-template-areas: "gallery" "details";

    @include tablet(min) {
      grid-template-rows: unset;
      grid-template-columns: 1fr 1fr;
      grid-template-areas: "gallery details";
      gap: 0 24px;
    }

    @include desktop-small(min) {
      gap: 0 32px;
    }

    @include desktop-medium(min) {
      gap: 0 40px;
    }
  }

  .productDetailsGridGallery {
    grid-area: gallery;
  }

  .productDetailsGridDetails {
    grid-area: details;
    padding: 0 16px;

    @include tablet(min) {
      padding: 0;
    }

    @include desktop-small(min) {
      padding-left: 32px;
    }

    @include desktop-medium(min) {
      padding: 0 40px;
    }
  }

  .productDetails {
    transition: 0.5s;

    .preloadDetails {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 500px;
      background: $color-white;
    }
  }
}

.bundleWrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 20px;
}

.productInformationsBlockTitle {
  display: block;
  margin-top: 20px;
  padding: 20px 16px;

  @include tablet(min) {
    margin-top: 40px;
    padding: 40px 16px 16px;
  }
}

.productInformationsBlock {
  margin-bottom: 20px;

  @include tablet(min) {
    margin-bottom: 40px;

    [data-aw-tab-accordion-tab]:first-child {
      margin-left: 16px;
    }
  }
}

.descriptionBlock {
  padding: 0 16px 20px;

  @include tablet(min) {
    padding: 0 40px 24px;
  }

  &Title {
    display: block;
    margin-bottom: 12px;

    @include tablet(min) {
      margin-bottom: 16px;
    }
  }

  &Body {
    @include font($font-weight-normal-v2, 12px, 16px);
    color: $color-text-secondary;

    @include tablet(min) {
      @include font(null, 14px, 20px);
    }
  }
}

.flixWrapper:not(:empty) {
  position: relative;
  overflow: hidden;
  margin-top: 80px;
}

.description {
  @include mobile(max) {
    padding-top: 20px;
  }
}
</style>
